import React, { useEffect, useRef, useState } from 'react';
import styles from './MultiSelectDropdown.module.css';

// Compact filter dropdown — looks like a `<select>`-style button until
// clicked, then drops a popover with a checkbox per option. Multi-select
// only; designed for filter strips where chip-style multi-selects waste
// horizontal space.
//
// Props:
//   label    - shown when nothing's selected (e.g. "Used In")
//   options  - array of strings
//   value    - array of strings (current selection)
//   onChange - called with the new array on every toggle
//   align    - 'left' (default) or 'right' — popover anchor

export default function MultiSelectDropdown({
  label = 'Filter',
  options,
  value,
  onChange,
  align = 'left',
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function toggle(opt) {
    if (value.includes(opt)) onChange(value.filter((v) => v !== opt));
    else onChange([...value, opt]);
  }
  function clearAll(e) {
    e.stopPropagation();
    onChange([]);
  }

  // Trigger label: "Used In: 2 selected" when something's picked,
  // just "Used In" when empty.
  const selectedCount = value.length;
  const triggerLabel = selectedCount > 0
    ? `${label}: ${selectedCount} selected`
    : label;

  return (
    <div ref={wrapRef} className={styles.wrap}>
      <button
        type="button"
        className={`${styles.trigger} ${selectedCount ? styles.triggerActive : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className={styles.triggerLabel}>{triggerLabel}</span>
        {selectedCount > 0 && (
          <span
            className={styles.clearBtn}
            onClick={clearAll}
            role="button"
            aria-label="Clear selection"
          >×</span>
        )}
        <ChevronIcon open={open} />
      </button>

      {open && (
        <div className={`${styles.popup} ${align === 'right' ? styles.popupRight : ''}`} role="menu">
          {options.length === 0 ? (
            <div className={styles.empty}>No options</div>
          ) : (
            options.map((opt) => {
              const checked = value.includes(opt);
              return (
                <label
                  key={opt}
                  className={`${styles.option} ${checked ? styles.optionChecked : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(opt)}
                  />
                  <span className={styles.optionLabel}>{opt}</span>
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

const ChevronIcon = ({ open }) => (
  <svg
    width="10" height="10" viewBox="0 0 10 10" fill="none"
    style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
  >
    <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);
